/**
 * One-off event editor (sheet), also used for "Aðeins þessi" edits of a series occurrence.
 *  - create → POST /api/events
 *  - edit   → PATCH /api/events/:id (only this event; occurrences become detached server-side)
 * Members always answer for themselves, so there is no "everyone is coming" default to set.
 * Mount it only while open (form state initialises on mount).
 */
import { useState } from 'react';
import { groupsFromMask, type AudienceGroup } from '../../../shared/audience';
import type { EventDTO, EventKind } from '../../../shared/dto/events';
import { uuid } from '../../../shared/ids';
import { dateTimeToMs, DAY_MS } from '../../../shared/time';
import { useCalendarMutations, type EventPatch } from '../../api/calendar';
import { serverNow } from '../../api/client';
import { useToast } from '../../components/toast';
import { Button, ErrorBox, Sheet, Switch, TextArea, TextInput } from '../../components/ui';
import { useT } from '../../i18n';
import { hhmmOf, minutesOf, todayStr } from '../../lib/dates';
import {
  AudienceChips,
  CheckinOpensSelect,
  DESCRIPTION_MAX,
  KindChips,
  LOCATION_MAX,
  TimeRangeFields,
  TITLE_MAX,
} from './EditorFields';
import { errorText, kindKey } from './shared';

export type EventEditorMode = { kind: 'create'; date?: string } | { kind: 'edit'; event: EventDTO };

interface Form {
  title: string;
  kind: EventKind;
  date: string;
  startTime: string;
  endTime: string;
  location: string;
  description: string;
  audience: AudienceGroup[];
  checkinOpensMin: number;
  setlistId: string;
  countsAttendance: boolean;
}

type Errors = Partial<Record<'title' | 'date' | 'time' | 'audience', string>>;

function initialForm(mode: EventEditorMode, titleFor: (k: EventKind) => string): Form {
  if (mode.kind === 'edit') {
    const ev = mode.event;
    return {
      title: ev.title,
      kind: ev.kind,
      date: todayStr(ev.startsAt),
      startTime: hhmmOf(ev.startsAt),
      endTime: hhmmOf(ev.endsAt),
      location: ev.location ?? '',
      description: ev.description ?? '',
      audience: groupsFromMask(ev.audienceMask),
      checkinOpensMin: ev.checkinOpensMin,
      setlistId: ev.setlistId ?? '',
      countsAttendance: ev.countsAttendance,
    };
  }
  return {
    title: titleFor('concert'),
    kind: 'concert',
    date: mode.date ?? todayStr(serverNow()),
    startTime: '20:00',
    endTime: '22:00',
    location: '',
    description: '',
    audience: ['all'],
    checkinOpensMin: 30,
    setlistId: '',
    countsAttendance: true,
  };
}

export function EventEditor({ mode, onClose, onSaved }: { mode: EventEditorMode; onClose: () => void; onSaved?: (r: { id: string; date: string }) => void }) {
  const t = useT();
  const toast = useToast();
  const m = useCalendarMutations();
  const [form, setForm] = useState<Form>(() => initialForm(mode, (k) => t(kindKey(k))));
  const [titleTouched, setTitleTouched] = useState(mode.kind === 'edit');
  const [errors, setErrors] = useState<Errors>({});
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [newId] = useState(uuid);
  const busy = m.createEvent.isPending || m.patchEvent.isPending;
  const isOccurrence = mode.kind === 'edit' && mode.event.seriesId !== null;

  const set = <K extends keyof Form>(k: K, v: Form[K]) => setForm((f) => ({ ...f, [k]: v }));
  const setKind = (kind: EventKind) =>
    setForm((f) => ({
      ...f,
      kind,
      title: titleTouched ? f.title : t(kindKey(kind)),
    }));

  const validate = (): Errors => {
    const e: Errors = {};
    if (!form.title.trim()) e.title = t('calendar.form.err_title');
    if (!/^\d{4}-\d{2}-\d{2}$/.test(form.date)) e.date = t('calendar.form.err_date');
    if (!/^\d{2}:\d{2}$/.test(form.startTime) || !/^\d{2}:\d{2}$/.test(form.endTime)) e.time = t('calendar.form.err_time');
    if (form.audience.length === 0) e.audience = t('calendar.form.err_audience');
    return e;
  };

  const save = async () => {
    const e = validate();
    setErrors(e);
    setSubmitError(null);
    if (Object.keys(e).length > 0) return;
    const startsAt = dateTimeToMs(form.date, form.startTime);
    let endsAt = dateTimeToMs(form.date, form.endTime);
    if (minutesOf(form.endTime) <= minutesOf(form.startTime)) endsAt += DAY_MS;
    const body: Required<Omit<EventPatch, 'defaultRsvp'>> = {
      title: form.title.trim(),
      kind: form.kind,
      location: form.location.trim() || null,
      description: form.description.trim() || null,
      audience: form.audience,
      startsAt,
      endsAt,
      setlistId: form.setlistId || null,
      checkinOpensMin: form.checkinOpensMin,
      countsAttendance: form.countsAttendance,
    };
    try {
      if (mode.kind === 'create') {
        await m.createEvent.mutateAsync({ id: newId, ...body });
        onSaved?.({ id: newId, date: form.date });
      } else {
        await m.patchEvent.mutateAsync({ id: mode.event.id, patch: body });
        onSaved?.({ id: mode.event.id, date: form.date });
      }
      toast.show(t('calendar.form.event_saved'), { tone: 'ok' });
      onClose();
    } catch (err) {
      setSubmitError(errorText(t, err));
    }
  };

  const title = mode.kind === 'create' ? t('calendar.form.new_event') : isOccurrence ? t('calendar.form.edit_occurrence') : t('calendar.form.edit_event');

  return (
    <Sheet
      open
      wide
      onClose={onClose}
      title={title}
      footer={
        <>
          <Button variant="secondary" block onClick={onClose} disabled={busy}>
            {t('common.cancel')}
          </Button>
          <Button block busy={busy} onClick={() => void save()}>
            {t('common.save')}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-5 py-2">
        <KindChips value={form.kind} onChange={setKind} />
        <TextInput
          label={t('calendar.form.title')}
          value={form.title}
          maxLength={TITLE_MAX}
          error={errors.title}
          onChange={(e) => {
            setTitleTouched(true);
            set('title', e.target.value);
          }}
        />
        <TextInput type="date" label={t('calendar.form.date')} value={form.date} error={errors.date} onChange={(e) => set('date', e.target.value)} required />
        <TimeRangeFields
          start={form.startTime}
          end={form.endTime}
          onStart={(v) => set('startTime', v)}
          onEnd={(v) => set('endTime', v)}
          error={errors.time}
        />
        <TextInput
          label={t('calendar.form.location')}
          placeholder={t('calendar.form.location_ph')}
          value={form.location}
          maxLength={LOCATION_MAX}
          onChange={(e) => set('location', e.target.value)}
        />
        <TextArea
          label={t('calendar.form.description')}
          placeholder={t('calendar.form.description_ph')}
          value={form.description}
          maxLength={DESCRIPTION_MAX}
          rows={3}
          onChange={(e) => set('description', e.target.value)}
        />
        <AudienceChips value={form.audience} onChange={(v) => set('audience', v)} error={errors.audience} />
        <Switch
          checked={form.countsAttendance}
          onChange={(v) => set('countsAttendance', v)}
          label={t('calendar.form.counts_attendance')}
          description={t('calendar.form.counts_attendance_hint')}
        />
        {/* The songs to practise are chosen on the event page (practice/ProgramCard). */}
        <div className="grid gap-4 sm:grid-cols-2">
          <CheckinOpensSelect value={form.checkinOpensMin} onChange={(v) => set('checkinOpensMin', v)} />
        </div>
        {submitError ? <ErrorBox>{submitError}</ErrorBox> : null}
      </div>
    </Sheet>
  );
}
